import { PickupDeliveryLocation } from '@/features/locations'

import { getFullAddress } from './getFullAddress'
import { HouseShipment, HouseShipmentNode, HouseShipmentTransportPoint } from '../domain/houseShipment'

const isDoorNode = (node: HouseShipmentNode): boolean =>
  node.type === 'place-of-pickup' || node.type === 'place-of-delivery'

export type CargoDoorLocations = {
  pickup?: HouseShipmentTransportPoint
  delivery?: HouseShipmentTransportPoint
}

export type CargoDoorLocationMap = Map<string, CargoDoorLocations>

const carries = (node: HouseShipmentNode, cargoID: string): boolean =>
  node.outgoingConnections.some((connection) => connection.houseCargoIDs.includes(cargoID))

/**
 * A shipment routes cargo per container, so a pickup node is the source of the edges carrying that
 * cargo while a delivery node is their target. Walking both directions keeps each container on its
 * own door, which is what lets one shipment show several pickup or delivery addresses.
 */
export const getCargoDoorLocations = (shipment: HouseShipment): CargoDoorLocationMap => {
  const pickupNodes = shipment.nodes.filter((node) => node.type === 'place-of-pickup')
  const deliveryNodes = shipment.nodes.filter((node) => node.type === 'place-of-delivery')
  const deliveryNodeIDs = new Set(deliveryNodes.map((node) => node.id))

  return new Map(
    shipment.houseCargos.map((houseCargo) => {
      const pickup = pickupNodes.find((node) => carries(node, houseCargo.ID))

      const deliveryNodeID = shipment.nodes
        .flatMap((node) => node.outgoingConnections)
        .find(
          (connection) =>
            connection.targetNodeID != null &&
            deliveryNodeIDs.has(connection.targetNodeID) &&
            connection.houseCargoIDs.includes(houseCargo.ID)
        )?.targetNodeID

      const delivery = deliveryNodes.find((node) => node.id === deliveryNodeID)

      return [houseCargo.ID, { pickup: pickup?.transportPoint, delivery: delivery?.transportPoint }]
    })
  )
}

export const getDoorLocationIDs = (cargoDoorLocations: CargoDoorLocationMap): string[] => [
  ...new Set(
    [...cargoDoorLocations.values()]
      .flatMap(({ pickup, delivery }) => [pickup?.locationID, delivery?.locationID])
      .filter((locationID): locationID is string => locationID != null)
  ),
]

/** Master-data location IDs referenced by the shipment's pickup and delivery nodes. */
export const getNodeLocationIDs = (nodes: HouseShipmentNode[]): string[] => [
  ...new Set(
    nodes
      .filter(isDoorNode)
      .map((node) => node.transportPoint.locationID)
      .filter((locationID): locationID is string => locationID != null)
  ),
]

/**
 * A door node carries either a master data location ID or an inline address, never both, so the
 * resolved locations have to be consulted before falling back to the address on the node.
 */
export const getDoorLocationLabel = (
  transportPoint: HouseShipmentTransportPoint | undefined,
  resolvedLocations: Map<string, PickupDeliveryLocation>
): string | undefined => {
  if (transportPoint == null) {
    return undefined
  }

  if (transportPoint.locationID != null) {
    const resolved = resolvedLocations.get(transportPoint.locationID)
    if (resolved) {
      return resolved.addressFormatted || resolved.locationName
    }
  }

  return getFullAddress(transportPoint.address) || undefined
}

export const getDoorLocationCountryCode = (
  transportPoint: HouseShipmentTransportPoint | undefined,
  resolvedLocations: Map<string, PickupDeliveryLocation>
): string | undefined => {
  if (transportPoint == null) {
    return undefined
  }

  if (transportPoint.locationID != null) {
    const resolved = resolvedLocations.get(transportPoint.locationID)
    if (resolved) {
      return resolved.address.countryCode
    }
  }

  return transportPoint.address?.countryCode
}

export type CargoDoorLabels = { pickup?: string; delivery?: string }

export const getCargoDoorLabels = (
  cargoDoorLocations: CargoDoorLocationMap,
  resolvedLocations: Map<string, PickupDeliveryLocation>
): Map<string, CargoDoorLabels> =>
  new Map(
    [...cargoDoorLocations].map(([cargoID, { pickup, delivery }]) => [
      cargoID,
      {
        pickup: getDoorLocationLabel(pickup, resolvedLocations),
        delivery: getDoorLocationLabel(delivery, resolvedLocations),
      },
    ])
  )

export const hasAnyDoorLabel = (doorLabels: Map<string, CargoDoorLabels>, side: keyof CargoDoorLabels): boolean =>
  [...doorLabels.values()].some((labels) => labels[side] != null)
